// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
export var LOGIN_HINT_PREFIX = "login_hint__";
export var MAX_LOGIN_HINT_COUNT = 5;
export var MAX_LOGIN_HINT_NAME_LENGTH = 128;
export var MAX_LOGIN_HINT_VALUE_LENGTH = 255;
var LOGIN_HINT_NAME_PATTERN = /^[A-Za-z0-9._-]+$/;
var HEX_PAIR_PATTERN = /^[0-9A-Fa-f]{2}$/;
var InvalidLoginHintsError = /** @class */ (function (_super) {
    __extends(InvalidLoginHintsError, _super);
    function InvalidLoginHintsError() {
        var _this = _super.call(this, "Invalid login hint parameters") || this;
        _this.name = "InvalidLoginHintsError";
        return _this;
    }
    return InvalidLoginHintsError;
}(Error));
export { InvalidLoginHintsError };
export var routeAcceptsLoginHints = function (pathname) {
    return /\/(login|enroll)\/?$/.test(pathname);
};
var ENROLL_ROUTE_PATTERN = /\/enroll\/?$/;
/**
 * Returns where Keycloak sends the voter once enrollment succeeds, or undefined to keep the
 * adapter default. Kept beside {@link routeAcceptsLoginHints} because both must accept the same
 * optional trailing slash: a link good enough to carry hints has to reach /login afterwards.
 */
export var enrollmentRedirectUrl = function (baseUrl, search) {
    return ENROLL_ROUTE_PATTERN.test(baseUrl)
        ? baseUrl.replace(ENROLL_ROUTE_PATTERN, "/login") + search
        : undefined;
};
// Decode only enough ASCII bytes to recognize the namespace. This remains reliable when a later
// percent escape is malformed, which is precisely when the invalid-link cleanup must fail closed.
var rawNameHasLoginHintPrefix = function (rawName) {
    var rawIndex = 0;
    for (var prefixIndex = 0; prefixIndex < LOGIN_HINT_PREFIX.length; prefixIndex += 1) {
        if (rawIndex >= rawName.length) {
            return false;
        }
        var character = rawName[rawIndex];
        if (character === "%") {
            var hexPair = rawName.slice(rawIndex + 1, rawIndex + 3);
            if (!HEX_PAIR_PATTERN.test(hexPair)) {
                return false;
            }
            character = String.fromCharCode(Number.parseInt(hexPair, 16));
            rawIndex += 3;
        }
        else {
            character = character === "+" ? " " : character;
            rawIndex += 1;
        }
        if (character !== LOGIN_HINT_PREFIX[prefixIndex]) {
            return false;
        }
    }
    return true;
};
var validateLoginHint = function (name, value) {
    if (!name ||
        name.length > MAX_LOGIN_HINT_NAME_LENGTH ||
        !LOGIN_HINT_NAME_PATTERN.test(name) ||
        !value.trim() ||
        value.length > MAX_LOGIN_HINT_VALUE_LENGTH) {
        throw new InvalidLoginHintsError();
    }
};
var validateLoginHintEncoding = function (search) {
    for (var _i = 0, _a = search.replace(/^\?/, "").split("&"); _i < _a.length; _i++) {
        var component = _a[_i];
        var separatorIndex = component.indexOf("=");
        var rawName = separatorIndex === -1 ? component : component.slice(0, separatorIndex);
        var rawValue = separatorIndex === -1 ? "" : component.slice(separatorIndex + 1);
        var parameterName = void 0;
        try {
            parameterName = decodeURIComponent(rawName.replaceAll("+", " "));
        }
        catch (_b) {
            if (rawNameHasLoginHintPrefix(rawName)) {
                throw new InvalidLoginHintsError();
            }
            continue;
        }
        if (parameterName.startsWith(LOGIN_HINT_PREFIX)) {
            try {
                decodeURIComponent(rawValue.replaceAll("+", " "));
            }
            catch (_c) {
                throw new InvalidLoginHintsError();
            }
        }
    }
};
export var parseLoginHints = function (search) {
    validateLoginHintEncoding(search);
    var searchParams = new URLSearchParams(search);
    var hints = new Map();
    // The portal is compiled targeting ES5, where `for ... of` over an iterator
    // (as opposed to an array) is transpiled into a no-op indexed loop.
    searchParams.forEach(function (value, parameterName) {
        if (!parameterName.startsWith(LOGIN_HINT_PREFIX)) {
            return;
        }
        var hintName = parameterName.slice(LOGIN_HINT_PREFIX.length);
        validateLoginHint(hintName, value);
        if (hints.has(hintName)) {
            throw new InvalidLoginHintsError();
        }
        hints.set(hintName, value);
        if (hints.size > MAX_LOGIN_HINT_COUNT) {
            throw new InvalidLoginHintsError();
        }
    });
    hints.forEach(function (_value, hintName) {
        searchParams.delete("".concat(LOGIN_HINT_PREFIX).concat(hintName));
    });
    var remainingSearch = searchParams.toString();
    return {
        hints: Object.fromEntries(hints),
        remainingSearch: remainingSearch ? "?".concat(remainingSearch) : "",
    };
};
export var removeLoginHintsFromSearch = function (search) {
    var remainingComponents = search
        .replace(/^\?/, "")
        .split("&")
        .filter(function (component) {
        var separatorIndex = component.indexOf("=");
        var rawName = separatorIndex === -1 ? component : component.slice(0, separatorIndex);
        return !rawNameHasLoginHintPrefix(rawName);
    });
    return remainingComponents.length > 0 ? "?".concat(remainingComponents.join("&")) : "";
};
export var appendLoginHints = function (url, hints) {
    var result = new URL(url);
    var hintEntries = Object.entries(hints);
    if (hintEntries.length > MAX_LOGIN_HINT_COUNT) {
        throw new InvalidLoginHintsError();
    }
    for (var _i = 0, hintEntries_1 = hintEntries; _i < hintEntries_1.length; _i++) {
        var _a = hintEntries_1[_i], name = _a[0], value = _a[1];
        validateLoginHint(name, value);
        result.searchParams.set("".concat(LOGIN_HINT_PREFIX).concat(name), value);
    }
    return result.toString();
};
